import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UserIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import UserAvatar from './common/UserAvatar';

const UserDropdown = ({ user, onLogout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleViewProfile = () => {
    setIsOpen(false);
    const username = user?.username || user?.email?.split('@')[0] || 'profile';
    navigate(`/profile/${username}`);
  };

  const handleAccountSettings = () => {
    setIsOpen(false);
    navigate('/account-settings');
  };

  const handleSignOut = () => {
    setIsOpen(false);
    onLogout();
  };

  const displayName = user?.profile_name || user?.full_name || user?.email || 'User';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-2 py-1.5 text-secondary hover:text-primary hover:bg-pine-tint rounded-md transition-colors duration-300"
      >
        <UserAvatar
          userId={user?.user_id}
          src={user?.avatar_url}
          alt={displayName}
          size="xs"
        />

        <div className="hidden sm:flex flex-col items-start max-w-[140px]">
          <span className="text-body font-medium text-primary truncate w-full">
            {displayName}
          </span>
          <span className="text-label text-secondary truncate w-full">{user?.email}</span>
        </div>

        <ChevronDownIcon
          className={`w-4 h-4 shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="absolute right-0 mt-2 w-56 bg-bone-raised border border-hairline rounded-lg overflow-hidden z-50"
          >
            <div className="px-4 py-3 border-b border-hairline">
              <div className="flex items-center gap-3">
                <UserAvatar
                  userId={user?.user_id}
                  src={user?.avatar_url}
                  alt={displayName}
                  size="sm"
                />
                <div className="min-w-0">
                  <p className="text-body font-medium text-primary truncate">{displayName}</p>
                  <p className="text-label text-secondary truncate">{user?.email}</p>
                </div>
              </div>
            </div>

            <div className="py-1">
              <button
                type="button"
                onClick={handleViewProfile}
                className="flex items-center gap-3 w-full px-4 py-3 text-body text-secondary hover:text-primary hover:bg-pine-tint transition-colors duration-300"
              >
                <UserIcon className="w-5 h-5" />
                View profile
              </button>
              <button
                type="button"
                onClick={handleAccountSettings}
                className="flex items-center gap-3 w-full px-4 py-3 text-body text-secondary hover:text-primary hover:bg-pine-tint transition-colors duration-300"
              >
                <Cog6ToothIcon className="w-5 h-5" />
                Account settings
              </button>
              <button
                type="button"
                onClick={handleSignOut}
                className="flex items-center gap-3 w-full px-4 py-3 text-body text-danger hover:bg-pine-tint transition-colors duration-300"
              >
                <ArrowRightOnRectangleIcon className="w-5 h-5" />
                Sign out
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserDropdown;
